import axios from 'axios';
import { API_ROOT, logApiError } from '@/store/api';

const VERSION_CHECK_THROTTLE_MS = 10000;
let inflightVersionCheck = null;
let lastVersionCheckAt = 0;

export default {
    namespaced: true,
    state: {
        availableProducts: [],
        productsVersion: null,
    },
    mutations: {
        updateAvailableProducts(state, availableProducts) {
            state.availableProducts = availableProducts;
        },
        updateProductsVersion(state, version) {
            state.productsVersion = version;
        },
        resetProductsCache(state) {
            state.availableProducts = [];
            state.productsVersion = null;
            inflightVersionCheck = null;
            lastVersionCheckAt = 0;
        },
    },
    getters: {
        available_products: state => state.availableProducts || [],
    },
    actions: {
        async prefetchProducts({ dispatch }) {
            return dispatch('checkProductsVersion');
        },
        async checkProductsVersion({ state, dispatch, commit }, { force = false } = {}) {
            if (inflightVersionCheck) {
                return inflightVersionCheck;
            }
            if (
                !force &&
                Date.now() - lastVersionCheckAt < VERSION_CHECK_THROTTLE_MS &&
                state.availableProducts.length > 0
            ) {
                return state.availableProducts;
            }
            inflightVersionCheck = (async () => {
                try {
                    const version = await dispatch('getProductsVersion');
                    if (version === null) {
                        if (state.availableProducts.length > 0) {
                            return state.availableProducts;
                        }
                        const data = await dispatch('getActiveProducts');
                        commit('updateAvailableProducts', data);
                        return data;
                    }
                    if (!force && version === state.productsVersion && state.availableProducts.length > 0) {
                        return state.availableProducts;
                    }
                    const data = await dispatch('getActiveProducts');
                    commit('updateAvailableProducts', data);
                    commit('updateProductsVersion', version);
                    return data;
                } finally {
                    lastVersionCheckAt = Date.now();
                    inflightVersionCheck = null;
                }
            })();
            return inflightVersionCheck;
        },
        async getProductsVersion({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/configuration/products/version/`);
                return data.version;
            } catch (e) {
                logApiError(e, dispatch);
                return null;
            }
        },
        async getDashboardPlaylistItems({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/configuration/dashboard_playlist_items/`);
                // This list endpoint must yield an array. Guard against a
                // misrouted response (e.g. HTML from the SPA catch-all) so
                // downstream `.map` consumers on the dashboard can't crash.
                return Array.isArray(data) ? data : [];
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async getActiveProducts({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/configuration/products/active/`);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return [];
            }
        },
        async getOnboardingProducts({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/configuration/products/onboarding/`);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async getSolarProjects({ dispatch }) {
            try {
                const { data } = await axios.get(`${API_ROOT}/configuration/solar/projects/`);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async getLoginCarouselSlides() {
            try {
                const { data } = await axios.get(`${API_ROOT}/login-carousel/`);
                return data;
            } catch {
                return [];
            }
        },
        async getSolarProject({ dispatch }, payload) {
            try {
                if (payload.slug !== undefined) {
                    const { data } = await axios.get(`${API_ROOT}/configuration/solar/projects/${payload.slug}/`);
                    return data;
                } else {
                    const { data } = await axios.get(`${API_ROOT}/configuration/solar/projects/legacy/${payload.id}/`);
                    return data;
                }
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
    },
};
